import { useState, useEffect, useCallback } from 'react';
import { Table, Badge, Button, Modal, Select, Pagination } from '../../components/ui';

const MEDIA_URL = process.env.REACT_APP_MEDIA_URL || 'http://localhost:5001';
const LMS_URL   = process.env.REACT_APP_LMS_URL   || 'http://localhost:5004/api/v1';

const STATUS_COLORS = {
  submitted:          { color:'#4e9cf5', label:'Submitted' },
  under_review:       { color:'#e8a43a', label:'Under Review' },
  approved:           { color:'#3ecf8e', label:'Approved' },
  rejected:           { color:'#ef4444', label:'Needs Revision' },
  resubmit_requested: { color:'#ef4444', label:'Resubmit Requested' },
};

const TYPE_ICONS = {
  assignment:         '📝',
  practical_evidence: '📸',
  portfolio:          '🗂',
  quiz:               '🧠',
};

export default function SubmissionsPage() {
  const [tab, setTab]               = useState('submissions');
  const [submissions, setSubm]      = useState([]);
  const [quizAttempts, setAttempts] = useState([]);
  const [pagination, setPag]        = useState(null);
  const [loading, setLoading]       = useState(true);
  const [filter, setFilter]         = useState({ status: '', lesson_type: '' });
  const [quizFilter, setQuizFilter] = useState({ course_id: '' });
  const [courses, setCourses]       = useState([]);
  const [selected, setSel]          = useState(null);
  const [modal, setModal]           = useState(null);
  const [gradeForm, setGradeForm]   = useState({ score: '', feedback: '', status: 'approved' });
  const [saving, setSaving]         = useState(false);
  const [page, setPage]             = useState(1);

  const token = localStorage.getItem('mh_token');
  const headers = { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` };

  const loadSubmissions = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page, limit: 20 });
      if (filter.status)      params.set('status', filter.status);
      if (filter.lesson_type) params.set('lesson_type', filter.lesson_type);
      const res  = await fetch(`${LMS_URL}/admin/submissions?${params}`, { headers });
      const data = await res.json();
      setSubm(data.data || []); setPag(data.pagination);
    } catch { setSubm([]); } finally { setLoading(false); }
  }, [page, filter]);

  const loadQuizAttempts = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: 50 });
      if (quizFilter.course_id) params.set('course_id', quizFilter.course_id);
      const res  = await fetch(`${LMS_URL}/admin/quiz-attempts?${params}`, { headers });
      const data = await res.json();
      setAttempts(data.data || []);
    } catch { setAttempts([]); } finally { setLoading(false); }
  }, [quizFilter]);

  const loadCourses = async () => {
    try {
      const res  = await fetch(`${LMS_URL}/courses`, { headers });
      const data = await res.json();
      setCourses(data.data || []);
    } catch {}
  };

  useEffect(() => { loadCourses(); }, []);
  useEffect(() => { if (tab === 'submissions') loadSubmissions(); }, [tab, loadSubmissions]);
  useEffect(() => { if (tab === 'quiz') loadQuizAttempts(); }, [tab, loadQuizAttempts]);

  const openGrade = (sub) => {
    setSel(sub);
    setGradeForm({ score: sub.score ?? '', feedback: sub.feedback || '', status: sub.status === 'approved' ? 'approved' : 'approved' });
    setModal('grade');
  };

  const handleGrade = async () => {
    if (!gradeForm.status) return;
    setSaving(true);
    try {
      const res = await fetch(`${LMS_URL}/admin/submissions/${selected.id}/grade`, {
        method: 'PUT', headers,
        body: JSON.stringify({
          status:   gradeForm.status,
          score:    gradeForm.score !== '' ? Number(gradeForm.score) : null,
          feedback: gradeForm.feedback || null,
        }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Grade failed'); return; }
      setModal(null); loadSubmissions();
    } catch (e) { alert('Failed: ' + e.message); } finally { setSaving(false); }
  };

  const resetQuizAttempts = async (lessonId, enrollmentId, learnerName) => {
    if (!window.confirm(`Reset all quiz attempts for ${learnerName}? They will be able to retake the quiz.`)) return;
    try {
      const res = await fetch(`${LMS_URL}/admin/quiz-attempts/reset`, {
        method: 'POST', headers,
        body: JSON.stringify({ lesson_id: lessonId, enrollment_id: enrollmentId }),
      });
      const data = await res.json();
      if (!res.ok) { alert(data.error || 'Reset failed'); return; }
      alert('✓ Quiz attempts reset successfully');
      loadQuizAttempts();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const SUBM_COLS = [
    { key: 'lesson_type', label: 'Type', render: v => (
      <span style={{ fontSize: 18 }}>{TYPE_ICONS[v] || '📎'}</span>
    )},
    { key: 'learner_name', label: 'Learner', render: (v, row) => (
      <div>
        <div style={{ fontWeight: 500 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.learner_email}</div>
      </div>
    )},
    { key: 'lesson_title', label: 'Lesson', render: (v, row) => (
      <div>
        <div style={{ fontSize: 13 }}>{v}</div>
        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{row.course_title}</div>
      </div>
    )},
    { key: 'status', label: 'Status', render: v => {
      const s = STATUS_COLORS[v] || { color: '#6b7280', label: v };
      return <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 10px', borderRadius: 20, background: `${s.color}18`, color: s.color, border: `1px solid ${s.color}33`, textTransform: 'capitalize' }}>{s.label}</span>;
    }},
    { key: 'score', label: 'Score', render: v => v !== null && v !== undefined
      ? <span style={{ fontWeight: 700, color: v >= 70 ? '#3ecf8e' : '#ef4444' }}>{v}/100</span>
      : <span style={{ color: 'var(--text-muted)' }}>—</span>
    },
    { key: 'submitted_at', label: 'Submitted', render: v => (
      <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>
        {new Date(v).toLocaleDateString('en-RW', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
      </span>
    )},
    { key: '_actions', label: '', render: (_, row) => (
      <div style={{ display: 'flex', gap: 6 }} onClick={e => e.stopPropagation()}>
        <Button variant="ghost" size="sm" onClick={() => openGrade(row)}>
          {['approved','rejected'].includes(row.status) ? 'Review' : 'Grade'}
        </Button>
      </div>
    )},
  ];

  const tabBtn = (t) => ({
    padding: '9px 18px', fontSize: 13, cursor: 'pointer', background: 'none', border: 'none',
    borderBottom: `2px solid ${tab === t ? 'var(--accent)' : 'transparent'}`,
    color: tab === t ? 'var(--accent)' : 'var(--text-muted)',
    fontWeight: tab === t ? 600 : 400, marginBottom: -1, transition: 'all 0.15s',
  });

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

      {/* Tabs */}
      <div style={{ display: 'flex', gap: 4, borderBottom: '1px solid var(--border)' }}>
        <button style={tabBtn('submissions')} onClick={() => setTab('submissions')}>
          Evidence & Assignments
          {submissions.filter(s => s.status === 'submitted').length > 0 && (
            <span style={{ marginLeft: 6, fontSize: 10, padding: '2px 6px', borderRadius: 20, background: 'rgba(232,164,58,0.2)', color: 'var(--accent)', border: '1px solid rgba(232,164,58,0.3)' }}>
              {submissions.filter(s => s.status === 'submitted').length} pending
            </span>
          )}
        </button>
        <button style={tabBtn('quiz')} onClick={() => setTab('quiz')}>Quiz Attempts</button>
      </div>

      {/* ── SUBMISSIONS TAB ── */}
      {tab === 'submissions' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <Select value={filter.status} onChange={e => setFilter(f => ({ ...f, status: e.target.value }))} style={{ width: 180 }}>
              <option value="">All Statuses</option>
              <option value="submitted">Pending Review</option>
              <option value="under_review">Under Review</option>
              <option value="approved">Approved</option>
              <option value="rejected">Rejected</option>
              <option value="resubmit_requested">Resubmit Requested</option>
            </Select>
            <Select value={filter.lesson_type} onChange={e => setFilter(f => ({ ...f, lesson_type: e.target.value }))} style={{ width: 180 }}>
              <option value="">All Types</option>
              <option value="assignment">Assignment</option>
              <option value="practical_evidence">Practical Evidence</option>
              <option value="portfolio">Portfolio</option>
            </Select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{pagination?.total ?? 0} submissions</span>
          </div>

          <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
            <Table columns={SUBM_COLS} data={submissions} loading={loading} onRowClick={openGrade}/>
            <Pagination pagination={pagination} onPage={setPage}/>
          </div>
        </>
      )}

      {/* ── QUIZ ATTEMPTS TAB ── */}
      {tab === 'quiz' && (
        <>
          <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
            <Select value={quizFilter.course_id} onChange={e => setQuizFilter({ course_id: e.target.value })} style={{ width: 260 }}>
              <option value="">All Courses</option>
              {courses.map(c => <option key={c.id} value={c.id}>{c.title}</option>)}
            </Select>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{quizAttempts.length} attempts</span>
          </div>

          {quizAttempts.length === 0 ? (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '48px', textAlign: 'center', color: 'var(--text-muted)' }}>
              No quiz attempts found.
            </div>
          ) : (
            <div style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border)' }}>
                    {['Learner', 'Quiz', 'Attempts', 'Best Score', 'Passed', 'Last Attempt', ''].map(h => (
                      <th key={h} style={{ padding: '10px 16px', textAlign: 'left', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {quizAttempts.map((a, i) => (
                    <tr key={i} style={{ borderBottom: '1px solid var(--border)', transition: 'background 0.1s' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-elevated)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontWeight: 500, fontSize: 13 }}>{a.learner_name}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.learner_email}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <div style={{ fontSize: 13 }}>{a.lesson_title}</div>
                        <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>{a.course_title}</div>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontWeight: 700, color: a.attempt_count >= a.max_attempts ? '#ef4444' : 'var(--text-primary)' }}>
                          {a.attempt_count}/{a.max_attempts}
                        </span>
                        {a.attempt_count >= a.max_attempts && (
                          <div style={{ fontSize: 10, color: '#ef4444', fontWeight: 600 }}>MAX REACHED</div>
                        )}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        <span style={{ fontWeight: 700, color: (a.best_score || 0) >= 70 ? '#3ecf8e' : '#ef4444' }}>
                          {a.best_score ?? 0}%
                        </span>
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {a.passed
                          ? <span style={{ fontSize: 11, fontWeight: 700, color: '#3ecf8e' }}>✓ Passed</span>
                          : <span style={{ fontSize: 11, color: 'var(--text-muted)' }}>Not yet</span>}
                      </td>
                      <td style={{ padding: '12px 16px', fontSize: 12, color: 'var(--text-muted)' }}>
                        {a.last_attempt ? new Date(a.last_attempt).toLocaleDateString('en-RW', { day: 'numeric', month: 'short' }) : '—'}
                      </td>
                      <td style={{ padding: '12px 16px' }}>
                        {a.attempt_count >= a.max_attempts && !a.passed && (
                          <Button variant="secondary" size="sm"
                            onClick={() => resetQuizAttempts(a.lesson_id, a.enrollment_id, a.learner_name)}>
                            Reset Attempts
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {/* ── GRADE MODAL ── */}
      <Modal open={modal === 'grade'} onClose={() => setModal(null)}
        title={`Grade — ${selected?.learner_name}`} width={640}>
        {selected && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>

            {/* Lesson info */}
            <div style={{ padding: '12px 16px', background: 'var(--bg-elevated)', borderRadius: 10, border: '1px solid var(--border)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
                <div>
                  <div style={{ fontWeight: 600, fontSize: 14 }}>{selected.lesson_title}</div>
                  <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{selected.course_title} · {selected.program_title}</div>
                </div>
                <span style={{ fontSize: 18 }}>{TYPE_ICONS[selected.lesson_type] || '📎'}</span>
              </div>
              <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                Submitted {new Date(selected.submitted_at).toLocaleDateString('en-RW', { day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </div>
            </div>

            {/* Submission content */}
            {selected.text_response && (
              <div>
                <div style={labelStyle}>Written Response</div>
                <div style={{ background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, padding: '12px 14px', fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.7, maxHeight: 200, overflowY: 'auto', whiteSpace: 'pre-wrap' }}>
                  {selected.text_response}
                </div>
              </div>
            )}

            {(selected.file_urls || []).filter(Boolean).length > 0 && (
              <div>
                <div style={labelStyle}>Uploaded Files ({(selected.file_urls || []).filter(Boolean).length})</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  {(selected.file_urls || []).filter(Boolean).map((url, i) => {
                    const isImage = /\.(jpg|jpeg|png|gif|webp)$/i.test(url);
                    const isVideo = /\.(mp4|webm|mov)$/i.test(url);
                    const isPDF   = /\.pdf$/i.test(url);
                    const fullUrl = url.startsWith('http') ? url : `${MEDIA_URL}${url}`;
                    const icon    = isVideo ? '🎥' : isPDF ? '📄' : '📎';
                    const label   = isVideo ? 'Video' : isPDF ? 'PDF' : 'File';
                    return (
                      <a key={i} href={fullUrl} target="_blank" rel="noopener noreferrer"
                        style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textDecoration: 'none', cursor: 'pointer' }}>
                        {isImage ? (
                          <img src={fullUrl} alt={`File ${i+1}`}
                            style={{ width: 120, height: 80, objectFit: 'cover', borderRadius: 8, border: '2px solid var(--border)', transition: 'border-color 0.15s, transform 0.15s' }}
                            onMouseEnter={e => { e.target.style.borderColor = 'var(--accent)'; e.target.style.transform = 'scale(1.03)'; }}
                            onMouseLeave={e => { e.target.style.borderColor = 'var(--border)';  e.target.style.transform = 'scale(1)'; }}/>
                        ) : (
                          <div style={{ width: 120, height: 80, borderRadius: 8, border: '2px solid var(--border)', background: 'var(--bg-elevated)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 4, transition: 'border-color 0.15s, background 0.15s' }}
                            onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--accent)'; e.currentTarget.style.background = 'var(--bg-surface)'; }}
                            onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)';  e.currentTarget.style.background = 'var(--bg-elevated)'; }}>
                            <span style={{ fontSize: 28 }}>{icon}</span>
                            <span style={{ fontSize: 9, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em' }}>{label}</span>
                          </div>
                        )}
                        <span style={{ fontSize: 11, color: 'var(--accent)', fontWeight: 500 }}>Open →</span>
                      </a>
                    );
                  })}
                </div>
              </div>
            )}

            {selected.link_url && (
              <div>
                <div style={labelStyle}>Submitted Link</div>
                <a href={selected.link_url} target="_blank" rel="noopener noreferrer"
                  style={{ fontSize: 13, color: 'var(--accent)', wordBreak: 'break-all' }}>
                  🔗 {selected.link_url}
                </a>
              </div>
            )}

            {/* Previous feedback */}
            {selected.feedback && (
              <div style={{ padding: '10px 14px', background: 'rgba(232,164,58,0.06)', border: '1px solid rgba(232,164,58,0.2)', borderRadius: 8 }}>
                <div style={labelStyle}>Previous Feedback</div>
                <div style={{ fontSize: 13, color: 'var(--text-secondary)', lineHeight: 1.6 }}>{selected.feedback}</div>
              </div>
            )}

            {/* Grade form */}
            <div style={{ borderTop: '1px solid var(--border)', paddingTop: 16, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
              <div>
                <label style={labelStyle}>Decision *</label>
                <select value={gradeForm.status} onChange={e => setGradeForm(f => ({ ...f, status: e.target.value }))}
                  style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13, fontFamily: 'inherit' }}>
                  <option value="approved">✓ Approve</option>
                  <option value="rejected">✗ Reject</option>
                  <option value="resubmit_requested">↩ Request Resubmission</option>
                </select>
              </div>
              <div>
                <label style={labelStyle}>Score (0–100, optional)</label>
                <input type="number" min={0} max={100} value={gradeForm.score}
                  onChange={e => setGradeForm(f => ({ ...f, score: e.target.value }))}
                  placeholder="e.g. 85"
                  style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box' }}/>
              </div>
              <div style={{ gridColumn: '1/-1' }}>
                <label style={labelStyle}>Feedback to learner</label>
                <textarea value={gradeForm.feedback} onChange={e => setGradeForm(f => ({ ...f, feedback: e.target.value }))}
                  rows={3} placeholder="Explain your decision, what was good, what needs improvement…"
                  style={{ width: '100%', background: 'var(--bg-elevated)', border: '1px solid var(--border)', borderRadius: 8, color: 'var(--text-primary)', padding: '9px 12px', fontSize: 13, resize: 'vertical', fontFamily: 'inherit', boxSizing: 'border-box' }}/>
              </div>
            </div>

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Button variant="secondary" onClick={() => setModal(null)}>Cancel</Button>
              <Button onClick={handleGrade} loading={saving}
                style={{ background: gradeForm.status === 'approved' ? '#3ecf8e' : gradeForm.status === 'rejected' ? '#ef4444' : 'var(--accent)', color: '#080a0d' }}>
                {gradeForm.status === 'approved' ? '✓ Approve' : gradeForm.status === 'rejected' ? '✗ Reject' : '↩ Request Resubmission'}
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

const labelStyle = { fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.07em', display: 'block', marginBottom: 6 };